import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Button from '../../components/Button/Button'
import Link from '../../components/Link/Link'
import LoadingModal from '../../components/Modals/LoadingModal'
import TextInput from '../../components/TextInput/TextInput'
import { emailRegEx, passwordRegEx } from '../../utility/functions'
import sso22Axios from '../../utility/sso22Axios'
import './../../styles/Pages/LoginPage.sass'
import { ROUTES } from '../../resources/routes-constants'
import { COLORS } from '../../resources/colors'
import { ReducerGraphic, Reducers } from '../../types/reducers'
import { useDispatch } from 'react-redux'
import { setLoggedUserAuthToken, setLoggedUserData } from '../../store/actions/user'

const RegisterPage: React.FC = () => {
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [registerError, setRegisterError] = useState('')
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const graphic = useSelector<Reducers, ReducerGraphic>((state) => state.graphic)
    const { t } = useTranslation()

    const registerUser = async () => {
        setIsLoading(true)
        try {
            const registrationCredentials = {
                firstName,
                lastName,
                email,
                username,
                password,
                passwordConfirm: confirmPassword,
                targetPlatform: 'FM-WORLD',
                language: graphic.currentLanguage,
            }
            const registeredUser = await sso22Axios
                .post('auth/signin', registrationCredentials)
                .then((response) => response.data)
            if (registeredUser.uuid) {
                dispatch(setLoggedUserData(registeredUser))
                dispatch(setLoggedUserAuthToken(registeredUser.authToken))
                navigate(`${ROUTES.HOMEPAGE_ROUTE}`)
            }
        } catch (error: any) {
            console.error(error)
            setRegisterError(error.toString())
        }
        setIsLoading(false)
    }

    return (
        <div className="u-full-page-container u-row-page" style={{ gap: 0 }}>
            <div className="c-login-interactive-panel">
                <div className="c-login-interactive-panel__main-panel">
                    <div className="o-login-inputs-container">
                        <p className="u-medium-text u-bold-text">{t('auth.registration')}</p>
                        <div className="u-normal-text">
                            {t('auth.have_account')}{' '}
                            <Link
                                label={t('auth.logon')}
                                underline="always"
                                type="accent"
                                target="internal"
                                targetUrl={{ url: ROUTES.LOGIN_ROUTE }}
                            />
                        </div>
                        <div className="u-elements-row u-small-gap">
                            <TextInput
                                value={firstName}
                                placeholder={t('auth.first_name') || ''}
                                onTextChange={(newValue) => setFirstName(newValue)}
                            />
                            <TextInput
                                value={lastName}
                                placeholder={t('auth.last_name') || ''}
                                onTextChange={(newValue) => setLastName(newValue)}
                            />
                        </div>
                        <TextInput
                            value={email}
                            placeholder={t('auth.email') || ''}
                            borderColor={email && !emailRegEx.test(email) ? COLORS.redDelete : ''}
                            onTextChange={(newValue) => setEmail(newValue)}
                        />
                        <TextInput
                            value={username}
                            placeholder={t('auth.username') || ''}
                            onTextChange={(newValue) => setUsername(newValue)}
                        />
                        <TextInput
                            type={'password'}
                            value={password}
                            placeholder={t('auth.password') || ''}
                            borderColor={password && !passwordRegEx.test(password) ? COLORS.redDelete : ''}
                            onTextChange={(newValue) => setPassword(newValue)}
                        />
                        <p className="u-normal-text">{t('auth.password_advice')}</p>
                        <TextInput
                            type={'password'}
                            value={confirmPassword}
                            placeholder={t('auth.confirm_password') || ''}
                            borderColor={password && password !== confirmPassword ? COLORS.redDelete : ''}
                            onTextChange={(newValue) => setConfirmPassword(newValue)}
                        />
                        <p className="u-normal-text u-bold-text">
                            {t('auth.create_account_policy_pre')}{' '}
                            <a href="https://www.fm-world.it/privacy/" target="_blank" rel="noreferrer">
                                {t('auth.privacy_policy')}
                            </a>
                            {t('auth.create_account_policy_post')}
                        </p>
                        {registerError && (
                            <p className="u-normal-text u-bold-text u-error-text">
                                {t('genral.error')}: {registerError}
                            </p>
                        )}
                        <Button
                            label={t('auth.create_account')}
                            type="submit"
                            onPress={async () => {
                                if (
                                    !isLoading &&
                                    email &&
                                    emailRegEx.test(email) &&
                                    password &&
                                    passwordRegEx.test(password) &&
                                    password === confirmPassword
                                ) {
                                    await registerUser()
                                }
                            }}
                            disabled={
                                !email ||
                                !password ||
                                password !== confirmPassword ||
                                !passwordRegEx.test(password) ||
                                !emailRegEx.test(email)
                            }
                        />
                    </div>
                </div>
            </div>
            <LoadingModal text={t('loadings.registering_account')} isVisible={isLoading} />
        </div>
    )
}

export default RegisterPage
